import { get } from 'lodash';
import { isExperienceApiModel } from '../../../common/helpers';
import type { DCPBadgeDTO, IEmicoData, SMSEnumDTO, Vehicle } from '../../shared/ocb';
import type { EmicoItem, FormattedData, LabelDescription, ProductDetail } from '../product-details.model';

type ProductDetailKeys =
  | 'badges'
  | 'baumuster'
  | 'bodyType'
  | 'brand'
  | 'code'
  | 'color'
  | 'commissionNumber'
  | 'name'
  | 'productType'
  | 'fuelType'
  | 'transmission'
  | 'color'
  | 'engineSizeInCcm'
  | 'numberOfCylinders'
  | 'modelYear'
  | 'length'
  | 'width'
  | 'height'
  | 'weight'
  | 'maxLoad'
  | 'maxWeight'
  | 'loadingSpaceLength'
  | 'loadingSpaceWidth'
  | 'loadingSpaceHeight'
  | 'loadingSpaceVolume'
  | 'maxSpeed'
  | 'driveType'
  | 'mileage'
  | 'numberOfDoors'
  | 'numberOfSeats'
  | 'upholstery'
  | 'emicoData'
  | 'emicoDataWltp'
  | 'vehicleId';

export const getProductDetailParam = <TParam extends ProductDetailKeys>(
  product: Partial<Vehicle> | Partial<ProductDetail>,
  param: TParam,
) => {
  const isVehicleModel = !isExperienceApiModel(product);

  if (isVehicleModel) {
    return get(product, param);
  }

  const productDetailMapper = {
    badges: labelDescription2DCPBadgeDTO(product.characteristics?.badges),
    baumuster: product.vehicleModel?.baumuster,
    bodyType: formattedData2SMSEnumDTO(product.vehicleModel?.bodyType),
    brand: product.vehicleModel?.brand?.value,
    code: product.identification?.code,
    color: product.color?.text,
    vehicleId: product.identification?.productId,
    commissionNumber: product.identification?.commissionNumber,
    name: product.vehicleModel?.name,
    productType: product.identification?.productType,
    fuelType: formattedData2SMSEnumDTO(product.technicalInformation?.engine?.fuelType),
    transmission: product.technicalInformation?.transmission?.transmissionCategory?.value,
    numberOfCylinders: product.technicalInformation?.engine?.cylinder?.value,
    modelYear: product.vehicleModel?.modelYear,
    width: product.technicalInformation?.dimension?.formattedValue,
    weight: product.technicalInformation?.weights?.unloadedWeight?.formattedValue,
    maxLoad: product.technicalInformation?.payload?.formattedValue,
    maxWeight: product.technicalInformation?.weights?.maximumWeight?.formattedValue,
    maxSpeed: product.technicalInformation?.topSpeed?.formattedValue,
    driveType: formattedData2SMSEnumDTO(product.technicalInformation?.engine?.typeOfPropulsion),
    numberOfDoors: product.technicalInformation?.doors?.value,
    numberOfSeats: product.technicalInformation?.seats?.value,
    emicoData: mapEmicoData(product.emico?.nefz),
    emicoDataWltp: mapEmicoData(product.emico?.wtlp),
  } as const;

  return get(productDetailMapper, param);
};

const formattedData2SMSEnumDTO = (formattedData?: FormattedData<string>): SMSEnumDTO | undefined => {
  if (!formattedData) return;

  return {
    code: formattedData.value,
    name: formattedData.formattedValue,
  } as SMSEnumDTO;
};

const labelDescription2DCPBadgeDTO = (badges?: LabelDescription[]): DCPBadgeDTO[] | undefined => {
  if (!badges) return;

  return badges.map((badge) => ({ name: badge.label, description: badge.description })) as DCPBadgeDTO[];
};

const mapEmicoData = (data?: EmicoItem) =>
  data?.attributes?.map(
    (attributes) =>
      ({
        ...attributes,
        displayImage: attributes.displayImage?.url,
        footnoteText: data?.footnotes,
        order: 0,
      } as IEmicoData),
  );
